import { ReactNode } from "react";
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";
import styled from "styled-components";

import { FormError } from "../FormError";
import { FormLabel } from "../FormLabel";

const TextField = styled.textarea<{ _theme: "light" | "dark" }>`
  padding: 8px 10px;
  border: ${props => (props._theme === "light" ? "1px solid #f2f2f2" : "1px solid #fff")};
  background: ${props => (props._theme === "light" ? "#f2f2f2" : "#fff")};
  font-size: 14px;
  color: #333;
  border-radius: 10px;
  box-shadow: none;
  transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;
  outline: none;

  &:focus {
    background: #fff;
    border: 1px solid #4c9aff;
  }

  &:hover {
    background: #e9ebef;
    border: 1px solid #dee0e6;

    &:focus-within {
      background: #fff;
      border: 1px solid #4c9aff;
    }
  }
`;

type TextAreaProps<TFieldValues extends FieldValues, TContext extends object> = {
  className?: string;
  id: string;
  name?: FieldPath<TFieldValues>;
  placeholder?: string;
  title?: ReactNode;
  titleClasses?: string;
  subTitle?: string;
  control: Control<TFieldValues, TContext>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.ChangeEventHandler<HTMLInputElement>;
  error?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  rows?: number;
  children?: React.ReactNode;
  value?: string;
  defaultValue?: string | number;
  theme?: "light" | "dark";
};

export function TextArea<TFieldValues extends FieldValues, TContext extends object>({
  rows = 5,
  id,
  name = id as FieldPath<TFieldValues>,
  control,
  placeholder,
  title,
  titleClasses,
  subTitle,
  disabled,
  readOnly,
  theme = "light"
}: TextAreaProps<TFieldValues, TContext>) {
  return (
    <div className="w-full mb-4">
      <div className="mb-2">
        {title ? (
          <FormLabel htmlFor={name} id={id} className={titleClasses}>
            {title}
          </FormLabel>
        ) : null}
        {subTitle ? <p className="text-sm font-light text-gray-600">{subTitle}</p> : null}
      </div>

      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => {
          return (
            <section className="flex flex-col w-full">
              <TextField
                placeholder={placeholder}
                disabled={disabled}
                readOnly={readOnly}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                rows={rows}
                _theme={theme}
              />
              {error ? <FormError error={error.message} id={name} /> : null}
            </section>
          );
        }}
      />
    </div>
  );
}
